import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import CryptoJS from "crypto-js";
import { FiActivity, FiMail, FiDownload } from "react-icons/fi";
import Navbar from "../../../common/components/HeaderOpsPage/Navbar";
import Body from "../../../common/components/Body/Body";
import "./MontenidoUsage.css";

const DOMAIN = "montenido.com";
const SECRET_KEY = "1234567890abcdef1234567890abcdef";

// Review thresholds — target is 10-15 calls per team lead per day.
const REVIEW_TARGET_ON = 10; // avg/day ≥ this = green
const REVIEW_TARGET_LOW = 5; // avg/day ≥ this = yellow; below = red

function decryptPayload(ciphertext) {
  const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
  return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
}

// Bucket a user into on-target / below / well-below based on their
// trailing 7-day average of distinct calls reviewed per day.
function reviewStatus(reviews_7d) {
  const avgPerDay = (reviews_7d || 0) / 7;
  if (avgPerDay >= REVIEW_TARGET_ON) return "on-target";
  if (avgPerDay >= REVIEW_TARGET_LOW) return "below";
  return "well-below";
}

const STATUS_LABEL = {
  "on-target": "On target",
  below: "Below target",
  "well-below": "Well below",
};

function csvEscape(v) {
  if (v == null) return "";
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toGeneralCsv(rows) {
  const header = "Email,FirstName,TotalHits,UniqueEndpoints,LastSeen\n";
  const body = rows
    .map((r) =>
      [
        csvEscape(r.Email),
        csvEscape(r.FirstName),
        r.hits,
        r.uniqueEndpoints,
        r.lastSeen ? new Date(r.lastSeen).toISOString() : "",
      ].join(",")
    )
    .join("\n");
  return header + body + "\n";
}

function toReviewsCsv(rows) {
  const header =
    "Email,FirstName,LastSeen,ReviewedToday,Reviewed7d,AvgPerDay,Status\n";
  const body = rows
    .map((r) => {
      const avg = ((r.reviews_7d || 0) / 7).toFixed(1);
      const status = STATUS_LABEL[reviewStatus(r.reviews_7d)];
      return [
        csvEscape(r.Email),
        csvEscape(r.FirstName),
        r.lastSeen ? new Date(r.lastSeen).toISOString() : "",
        r.reviews_today || 0,
        r.reviews_7d || 0,
        avg,
        status,
      ].join(",");
    })
    .join("\n");
  return header + body + "\n";
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const MontenidoUsage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("general"); // "general" | "reviews"

  const userRole = useSelector((state) => state.login?.login?.data?.data?.[0]?.Role);
  const userEmail =
    (useSelector((state) => state.login?.login?.data?.data?.[0]?.Email) || "").toLowerCase();
  const authToken = localStorage.getItem("authToken");

  const ALLOWED_OPS_DOMAINS = ["@montenido.com", "@magichealth.io"];
  const isAuthorizedOpsManager =
    userRole === "Operations Manager" &&
    ALLOWED_OPS_DOMAINS.some((d) => userEmail.endsWith(d));
  const isAuthorized = userRole === "Admin" || isAuthorizedOpsManager;

  useEffect(() => {
    if (!isAuthorized) {
      setError(
        "Access denied. This report is restricted to Admins or Operations Managers with a montenido.com or magichealth.io email."
      );
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_ANALYTICS_API}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        });
        if (!res.ok) throw new Error(`Analytics API returned ${res.status}`);
        const cipher = await res.text();
        setData(decryptPayload(cipher));
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthorized, authToken]);

  // Base rowset — one row per @montenido.com Operations Manager from the
  // directory, with activity + review counts layered on by lowercased email.
  const montenidoUsers = useMemo(() => {
    const directory = data?.montenidoDirectory || [];
    const activityRows = data?.allUsers || data?.topUsers || [];

    // Index activity by email (lowercased), merging rows that share an email
    // but differ by AdminID/FirstName (stale SQL / AdminID reassignment).
    const activityByEmail = new Map();
    for (const u of activityRows) {
      if (!u.Email) continue;
      const key = String(u.Email).toLowerCase();
      const existing = activityByEmail.get(key);
      if (existing) {
        existing.hits += u.hits || 0;
        existing.uniqueEndpoints = Math.max(
          existing.uniqueEndpoints,
          u.uniqueEndpoints || 0
        );
        if (
          u.lastSeen &&
          (!existing.lastSeen ||
            new Date(u.lastSeen) > new Date(existing.lastSeen))
        ) {
          existing.lastSeen = u.lastSeen;
        }
        // Prefer max (a user can appear on multiple activity rows; take the
        // higher review counts since these come from a single aggregation
        // and are identical across duplicates, but max is defensive).
        existing.reviews_today = Math.max(
          existing.reviews_today,
          u.reviews_today || 0
        );
        existing.reviews_7d = Math.max(existing.reviews_7d, u.reviews_7d || 0);
      } else {
        activityByEmail.set(key, {
          hits: u.hits || 0,
          uniqueEndpoints: u.uniqueEndpoints || 0,
          lastSeen: u.lastSeen || null,
          reviews_today: u.reviews_today || 0,
          reviews_7d: u.reviews_7d || 0,
        });
      }
    }

    const rowsFromDirectory = directory
      .filter(
        (d) => d.Email && String(d.Email).toLowerCase().endsWith(`@${DOMAIN}`)
      )
      .map((d) => {
        const act = activityByEmail.get(String(d.Email).toLowerCase()) || {
          hits: 0,
          uniqueEndpoints: 0,
          lastSeen: null,
          reviews_today: 0,
          reviews_7d: 0,
        };
        return {
          AdminID: d.AdminID,
          FirstName: d.FirstName || "Unknown",
          Email: d.Email,
          hits: act.hits,
          uniqueEndpoints: act.uniqueEndpoints,
          lastSeen: act.lastSeen,
          reviews_today: act.reviews_today,
          reviews_7d: act.reviews_7d,
        };
      });

    // Fallback path — if montenidoDirectory isn't in the response yet, fall
    // back to filtering activityRows directly.
    const rows =
      rowsFromDirectory.length > 0
        ? rowsFromDirectory
        : activityRows
            .filter(
              (u) =>
                u.Email &&
                String(u.Email).toLowerCase().endsWith(`@${DOMAIN}`)
            )
            .map((u) => ({
              AdminID: u.AdminID,
              FirstName: u.FirstName || "Unknown",
              Email: u.Email,
              hits: u.hits || 0,
              uniqueEndpoints: u.uniqueEndpoints || 0,
              lastSeen: u.lastSeen || null,
              reviews_today: u.reviews_today || 0,
              reviews_7d: u.reviews_7d || 0,
            }));

    return rows;
  }, [data]);

  // General Usage tab — sorted by last seen desc
  const generalUsers = useMemo(() => {
    return [...montenidoUsers].sort((a, b) => {
      const ta = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
      const tb = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
      return tb - ta;
    });
  }, [montenidoUsers]);

  // Call Reviews tab — sorted by reviews_today desc, then reviews_7d desc
  const reviewsUsers = useMemo(() => {
    return [...montenidoUsers].sort((a, b) => {
      const dt = (b.reviews_today || 0) - (a.reviews_today || 0);
      if (dt !== 0) return dt;
      return (b.reviews_7d || 0) - (a.reviews_7d || 0);
    });
  }, [montenidoUsers]);

  const totals = useMemo(() => {
    const totalHits = montenidoUsers.reduce((s, u) => s + (u.hits || 0), 0);
    const activeCount = montenidoUsers.filter((u) => (u.hits || 0) > 0).length;
    return {
      totalCount: montenidoUsers.length,
      activeCount,
      totalHits,
    };
  }, [montenidoUsers]);

  const reviewTotals = useMemo(() => {
    const teamLeads = montenidoUsers.length;
    const onTargetToday = montenidoUsers.filter(
      (u) => (u.reviews_today || 0) >= REVIEW_TARGET_ON
    ).length;
    const reviewsThisWeek = montenidoUsers.reduce(
      (s, u) => s + (u.reviews_7d || 0),
      0
    );
    const avgPerLeadPerDay =
      teamLeads > 0 ? (reviewsThisWeek / teamLeads / 7).toFixed(1) : "0.0";
    return {
      teamLeads,
      onTargetLabel: `${onTargetToday} / ${teamLeads}`,
      reviewsThisWeek,
      avgPerLeadPerDay,
    };
  }, [montenidoUsers]);

  const handleDownload = () => {
    const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    if (activeTab === "reviews") {
      downloadCsv(
        `montenido-call-reviews-${stamp}.csv`,
        toReviewsCsv(reviewsUsers)
      );
    } else {
      downloadCsv(`montenido-usage-${stamp}.csv`, toGeneralCsv(generalUsers));
    }
  };

  const currentRows = activeTab === "reviews" ? reviewsUsers : generalUsers;

  if (loading) {
    return (
      <div>
        <Navbar />
        <Body>
          <div className="mu-state">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p>Loading Montenido usage…</p>
          </div>
        </Body>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Navbar />
        <Body>
          <div className="mu-state">
            <FiActivity size={48} color="#ef4444" />
            <h2>Couldn't load usage report</h2>
            <p>{error}</p>
          </div>
        </Body>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <Body>
        <div className="mu-page">
          <div className="mu-header">
            <div>
              <h1>Montenido Usage Report</h1>
              <p className="mu-subtitle">
                All Operations Managers with <code>@{DOMAIN}</code> email
                addresses. Sorted by most recently active.
              </p>
            </div>
            <button
              className="mu-download-btn"
              onClick={handleDownload}
              disabled={currentRows.length === 0}
              type="button"
            >
              <FiDownload size={16} style={{ marginRight: 6 }} />
              Download CSV
            </button>
          </div>

          {/* Tabs */}
          <div className="mu-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "general"}
              className={`mu-tab ${activeTab === "general" ? "active" : ""}`}
              onClick={() => setActiveTab("general")}
            >
              General Usage
              <span className="mu-tab-count">{montenidoUsers.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === "reviews"}
              className={`mu-tab ${activeTab === "reviews" ? "active" : ""}`}
              onClick={() => setActiveTab("reviews")}
            >
              Call Reviews
              <span className="mu-tab-count">{montenidoUsers.length}</span>
            </button>
          </div>

          {/* Panel 1 — General Usage */}
          {activeTab === "general" && (
            <>
              <div className="mu-stats">
                <div className="mu-stat-card">
                  <div className="mu-stat-label">Montenido Users</div>
                  <div className="mu-stat-value">{totals.totalCount}</div>
                </div>
                <div className="mu-stat-card">
                  <div className="mu-stat-label">Active (last 30 days)</div>
                  <div className="mu-stat-value">{totals.activeCount}</div>
                </div>
                <div className="mu-stat-card">
                  <div className="mu-stat-label">Total Hits (last 30 days)</div>
                  <div className="mu-stat-value">
                    {totals.totalHits.toLocaleString()}
                  </div>
                </div>
              </div>

              {generalUsers.length === 0 ? (
                <div className="mu-empty">
                  <FiMail size={36} color="#94a3b8" />
                  <p>
                    No <code>@{DOMAIN}</code> users found in the directory.
                  </p>
                </div>
              ) : (
                <div className="mu-table-card">
                  <table className="mu-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>First Name</th>
                        <th>Total Hits</th>
                        <th>Unique Endpoints</th>
                        <th>Last Seen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generalUsers.map((u) => (
                        <tr key={u.AdminID}>
                          <td className="mu-email">{u.Email}</td>
                          <td>{u.FirstName}</td>
                          <td className="mu-numeric">
                            {u.hits.toLocaleString()}
                          </td>
                          <td className="mu-numeric">
                            {u.uniqueEndpoints ?? "—"}
                          </td>
                          <td>
                            {u.lastSeen
                              ? new Date(u.lastSeen).toLocaleString()
                              : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mu-footnote">
                <strong>Note:</strong> This is the initial release of the
                Montenido Usage report. Activity tracking begins as of this
                release, so the 30-day view will populate over time as usage
                accumulates.
              </div>
            </>
          )}

          {/* Panel 2 — Call Reviews */}
          {activeTab === "reviews" && (
            <>
              <div className="mu-stats mu-stats-4">
                <div className="mu-stat-card">
                  <div className="mu-stat-label">Team Leads</div>
                  <div className="mu-stat-value">{reviewTotals.teamLeads}</div>
                </div>
                <div className="mu-stat-card">
                  <div className="mu-stat-label">On Target Today</div>
                  <div className="mu-stat-value">
                    {reviewTotals.onTargetLabel}
                  </div>
                </div>
                <div className="mu-stat-card">
                  <div className="mu-stat-label">Reviews This Week</div>
                  <div className="mu-stat-value">
                    {reviewTotals.reviewsThisWeek}
                  </div>
                </div>
                <div className="mu-stat-card">
                  <div className="mu-stat-label">Avg / lead / day</div>
                  <div className="mu-stat-value">
                    {reviewTotals.avgPerLeadPerDay}
                  </div>
                </div>
              </div>

              <div className="mu-legend">
                <span>Status thresholds (7-day avg):</span>
                <span className="mu-legend-item">
                  <span className="mu-legend-dot mu-legend-green" />
                  ≥ {REVIEW_TARGET_ON} / day (on target)
                </span>
                <span className="mu-legend-item">
                  <span className="mu-legend-dot mu-legend-yellow" />
                  {REVIEW_TARGET_LOW} – {REVIEW_TARGET_ON - 1} / day (below)
                </span>
                <span className="mu-legend-item">
                  <span className="mu-legend-dot mu-legend-red" />
                  &lt; {REVIEW_TARGET_LOW} / day (well below)
                </span>
              </div>

              {reviewsUsers.length === 0 ? (
                <div className="mu-empty">
                  <FiMail size={36} color="#94a3b8" />
                  <p>
                    No <code>@{DOMAIN}</code> users found in the directory.
                  </p>
                </div>
              ) : (
                <div className="mu-table-card">
                  <table className="mu-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>First Name</th>
                        <th>Last Seen</th>
                        <th>Reviewed Today</th>
                        <th>Reviewed 7d</th>
                        <th>Avg / day</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reviewsUsers.map((u) => {
                        const avg = ((u.reviews_7d || 0) / 7).toFixed(1);
                        const status = reviewStatus(u.reviews_7d);
                        return (
                          <tr key={u.AdminID}>
                            <td className="mu-email">{u.Email}</td>
                            <td>{u.FirstName}</td>
                            <td>
                              {u.lastSeen
                                ? new Date(u.lastSeen).toLocaleString()
                                : "—"}
                            </td>
                            <td className="mu-numeric">
                              {u.reviews_today || 0}
                            </td>
                            <td className="mu-numeric">
                              {u.reviews_7d || 0}
                            </td>
                            <td className="mu-numeric">{avg}</td>
                            <td>
                              <span className={`mu-badge mu-badge-${status}`}>
                                <span className="mu-badge-dot" />
                                {STATUS_LABEL[status]}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="mu-footnote">
                <strong>Note:</strong> A review is one distinct call opened per
                team lead per day. Re-opens of the same call within a day count
                once. Target is {REVIEW_TARGET_ON}–15 reviews per team lead per
                day.
              </div>
            </>
          )}
        </div>
      </Body>
    </div>
  );
};

export default MontenidoUsage;
